<template>
  <v-app>
    <top-navigation-bar></top-navigation-bar>
    <div class="d-flex viewer-layout-container" style="height: 100%">
      <!-- Expandable Rail Navigation -->
      <ExpandableRail
        v-show="showNavMenu"
        @item-click="handleItemClick"
        @group-toggle="handleGroupToggle"
      />

      <!-- Multi-Panel Container -->
      <PanelContainer
        v-show="showNavMenu"
        @panel-resize="handlePanelResize"
        @panel-close="handlePanelClose"
        @collapse="handlePanelsCollapse"
      />

      <!-- Main Content Area (Push-over layout) -->
      <v-main
        class="flex-grow-1 main-content"
        :style="mainContentStyle"
      >
        <!-- Quick Settings Floating UI (shows in main viewer when visible and NOT pinned in top bar) -->
        <QuickSettingsFloating
          v-if="isQuickSettingsVisible && !isQuickSettingsPinnedInTopBar"
          :pinned="false"
          @close="handleQuickSettingsClose"
        />

        <slot />
      </v-main>

      <!-- Cropped Measurements Dialog -->
      <CroppedMeasurementsDialog />

      <!-- Wave C-α — singleton CML popup card (FR-012, FR-020 enforced by
           single-mount design). Both the Forge 3D viewer and the 360
           Pannellum viewer call openPopupCard(...) which drives this same
           instance. See specs/003-cml-phase2-popup-cards/plan.md §R-7.
           T016/T018/T019 — emits routed through CML_POPUP Vuex module. -->
      <CmlPopupCard
        @edit="onPopupEdit"
        @view-detail="onPopupViewDetail"
        @add-reading="onPopupAddReading"
        @create-task="onCmlCreateTask"
        @link-task="onCmlLinkTask"
      />

      <TaskLinkDialog
        v-model="cmlLinkDialogOpen"
        :cml-record-id="cmlTaskScope.cmlRecordId"
        :cml-no="cmlTaskScope.cmlNo" />

    </div>
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex';
import { updateMarkupsStyle } from '@Scripts/js/forgeService';
import { drawImage64OnPlane, drawImageOnPlane } from '@Scripts/js/forgeviewer';
import store from '@Store';
import TopNavigationBar from '@Share/components/TopNavigationBar.vue';
import CroppedMeasurementsDialog from '@/features/PotreeMeasurements/components/dialogs/CroppedMeasurementsDialog.vue';
import CmlPopupCard from '@/components/cml/CmlPopupCard.vue';
import { LinkedScanInfo } from '@/scripts/js/forgeModels';
import { PANEL_TAB } from '@/constants/tabs';
import { isRouteDashboardActive } from '@/constants/dashboards';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import permissions from '@Share/config/permissions';
import { $has } from '@/utils';
import TaskLinkDialog from '@/features/Tags/components/monitoring/TaskLinkDialog.vue';
import { closePopupCard } from '@/composables/usePopupCard';
import { readGhostAutoShow } from '@Share/components/QuickSettings/ghostOverlayPref';

// New Side Navigation Components
import {
  ExpandableRail,
  PanelContainer,
} from '@Share/components/SideNavigation';

// Quick Settings Floating UI
import { QuickSettingsFloating } from '@Share/components/QuickSettings';

const THREE = window.THREE;

export default {
  name: 'viewer-layout',

  components: {
    TopNavigationBar,
    CroppedMeasurementsDialog,
    ExpandableRail,
    PanelContainer,
    QuickSettingsFloating,
    CmlPopupCard,
    TaskLinkDialog,
  },

  data() {
    return {
      // AVA-437: add-task link-dialog state
      cmlLinkDialogOpen: false,
      /** @type {{ cmlRecordId: string, cmlNo: string }} */
      cmlTaskScope: { cmlRecordId: '', cmlNo: '' },
    };
  },

  created() {
    window.updateMarkupsStyle = updateMarkupsStyle;
    window.drawImage64OnPlane = drawImage64OnPlane;
    window.drawImageOnPlane = drawImageOnPlane;
    store.dispatch('GET_DATA_FROM_LOCAL_STORAGE');
    if ($has(permissions.Users.View))
  {
    store.dispatch('USER/GET');
  }

    // Permission-gated dispatches - only call APIs user has permission to access
    if ($has(permissions.TaskTracker.View)) {
      store.dispatch('TASK_MANAGER/GET_BY_USER');
    }
    if ($has(permissions.Registers.View)) {
      store.dispatch('REGISTERS/FETCH_USER_FACILITY_REGISTERS');
    }

    this.$store.dispatch('INITIALIZE_MAIN_DATA');
  },

  mounted() {
    // Listen for create inspect tag event from corrosion analysis
    this.$root.$on('create-inspect-tag-from-corrosion', this.handleCreateInspectFromCorrosion);
    // Listen for update inspect tag event from corrosion analysis
    this.$root.$on('update-inspect-tag-from-corrosion', this.handleUpdateInspectFromCorrosion);

    // Note: Escape key handling removed per design decision
    // Users should use the collapse toggle button or click-outside to collapse panels
  },

  beforeDestroy() {
    // Remove event listeners
    this.$root.$off('create-inspect-tag-from-corrosion', this.handleCreateInspectFromCorrosion);
    this.$root.$off('update-inspect-tag-from-corrosion', this.handleUpdateInspectFromCorrosion);

    this.$store.dispatch('CLEAR_MAIN_DATA');
  },

  watch: {
    // "Auto-show on ghosting" (default ON, per-device override in localStorage): when
    // ghosting turns on from anywhere — the viewer toolbar button, tag isolation, a
    // dashboard — surface the Quick Settings bar and request its ghost-style overlay so
    // the user can switch wireframe/grey without hunting through Display Settings. This
    // lives in the always-mounted layout because QuickSettingsFloating is v-if'd out
    // while the bar is hidden and so cannot react to this on its own.
    ghostingOnState(isOn, wasOn) {
      if (isOn && !wasOn && readGhostAutoShow()) {
        this.$store.commit('GENERAL/SET_QUICK_SETTINGS_VISIBLE', true);
        this.$store.commit('GENERAL/SET_GHOST_OVERLAY_OPEN_REQUEST', true);
      }
    },
  },

  computed: {
    ghostingOnState() {
      return this.$store.state.isGhostingOn;
    },

    ...mapGetters('GENERAL', {
      activeGroup: 'GET_ACTIVE_GROUP',
      isRailExpanded: 'GET_IS_RAIL_EXPANDED',
      panels: 'GET_PANELS',
      hasPanels: 'GET_HAS_PANELS',
      arePanelsCollapsed: 'GET_ARE_PANELS_COLLAPSED',
      isPinned: 'GET_IS_PINNED',
      totalNavWidth: 'GET_TOTAL_NAV_WIDTH',
      railWidth: 'GET_RAIL_WIDTH',
      isQuickSettingsVisible: 'GET_IS_QUICK_SETTINGS_VISIBLE',
      isQuickSettingsPinned: 'GET_IS_QUICK_SETTINGS_PINNED',
      inspectionFilterDockInset: 'GET_INSPECTION_FILTER_DOCK_INSET',
    }),

    canPinToTopBar() {
      return this.$store.getters['GENERAL/GET_CAN_PIN_QUICK_SETTINGS_TO_TOP_BAR'];
    },

    isQuickSettingsPinnedInTopBar() {
      return this.isQuickSettingsPinned && this.canPinToTopBar;
    },

    showNavMenu: {
      get() { return this.$store.getters['GET_SHOW_NAV_MENU']; },
      set(v) { this.$store.commit('SET_SHOW_NAV_MENU', v); },
    },

    /**
     * Get the viewer type for navigation confirmation
     */
    viewerType() {
      return this.$store.getters['GET_VIEWER_TYPE'];
    },

    /**
     * Dynamic main content style for push-over layout
     * Padding adjusts based on total navigation width
     */
    filterDockInset() {
      // Store getter handles mode/owner/compare/reality/fullscreen; the route-dashboard
      // term must be AND-ed in here because a Vuex getter cannot read $route.
      return isRouteDashboardActive(this.$route) ? 0 : this.inspectionFilterDockInset;
    },
    mainContentStyle() {
      const dockPad = `${this.filterDockInset}px`;
      const transition =
        'padding-left var(--ds-transition-rail), padding-right var(--ds-transition-rail)';
      if (!this.showNavMenu) {
        // Reality dashboard hides the nav but the dock inset must still apply (spec §F).
        return { paddingLeft: '0px', paddingRight: dockPad, transition };
      }

      // When panels are collapsed, only account for rail width
      const padding = this.arePanelsCollapsed
        ? this.railWidth
        : this.totalNavWidth;

      return {
        paddingLeft: `${padding}px`,
        paddingRight: dockPad,
        transition,
      };
    },
  },

  methods: {
    // ─── Wave C-α — popup card action handlers (T016 / T018 / T019) ─────────
    // AVA-437 Tier 2 — route by entityType so the TML popup (entityId = tmlPointId)
    // doesn't misroute as a cmlId. The CML_POPUP actions resolve the master-tag
    // context from { entityType, entityId, vm }.
    onPopupEdit({ entityType, entityId, vm }) {
      this.$store.dispatch('CML_POPUP/REQUEST_EDIT', { entityType, entityId, vm });
    },
    onPopupViewDetail({ entityType, entityId, vm }) {
      this.$store.dispatch('CML_POPUP/REQUEST_VIEW_DETAIL', { entityType, entityId, vm });
    },
    onPopupAddReading({ entityType, entityId, vm }) {
      this.$store.dispatch('CML_POPUP/REQUEST_ADD_READING', { entityType, entityId, vm });
    },

    // AVA-437: add-task — create-in-board and link-to-existing handlers --------
    onCmlCreateTask({ boardId, cmlRecordId, tmlPointId, cmlNo }) {
      if (!boardId || !cmlRecordId) {
        debugWarn('[View] onCmlCreateTask: missing boardId or cmlRecordId', { boardId, cmlRecordId });
        return;
      }
      // AVA-437 Tier 2 — scope to the real CML; narrow to the TmlPoint when present (TML popup).
      const scope = { cmlRecordId, label: cmlNo || '' };
      if (tmlPointId) scope.tmlPointId = tmlPointId;
      debugLog('[View] create task for CML', { boardId, scope });
      const boards = this.$store.getters['TASK_MANAGER/GET_ENTRIES_BY_USER'] || [];
      const board = boards.find((b) => b.id === boardId);
      const statusId = (board && board.statuses && board.statuses[0] && board.statuses[0].id) || null;
      const today = new Date().toISOString();
      const task = { typeId: boardId, text: '', start_date: today, end_date: today, forgeTagsIds: [], statusId };
      const editTask = this.$store.state.mainApp && this.$store.state.mainApp.$refs && this.$store.state.mainApp.$refs.editTask;
      if (!editTask) {
        debugError('[View] editTask ref not found on mainApp');
        return;
      }
      // Close the popup BEFORE opening the editor — the popup card is z-index:200 and would
      // otherwise occlude the EditTask dialog (the same root cause the old AddToPlan dialog hit).
      closePopupCard();
      editTask.open({ task, isNew: true, taskTypeId: boardId, seedCmlScopes: [scope] });
    },
    onCmlLinkTask({ cmlRecordId, tmlPointId, cmlNo }) {
      if (!cmlRecordId) {
        debugWarn('[View] onCmlLinkTask: missing cmlRecordId');
        return;
      }
      this.cmlTaskScope = { cmlRecordId, cmlNo: cmlNo || '' };
      if (tmlPointId) this.cmlTaskScope.tmlPointId = tmlPointId;
      // Close the popup first (z-index:200) so TaskLinkDialog isn't occluded by it.
      closePopupCard();
      this.$nextTick(() => { this.cmlLinkDialogOpen = true; });
    },

    /**
     * Handle group toggle from ExpandableRail
     */
    handleGroupToggle({ groupId, isExpanded }) {
      debugLog('[View] Group toggled:', groupId, 'expanded:', isExpanded);
    },

    /**
     * Handle menu item click from ExpandableRail
     * Opens the corresponding panel
     */
    handleItemClick({ itemId, panelId }) {
      debugLog('[View] Item clicked:', itemId, 'opening panel:', panelId);
    },

    /**
     * Handle panel resize event
     */
    handlePanelResize({ panelId, width }) {
      debugLog('[View] Panel resized:', panelId, 'width:', width);
    },

    /**
     * Handle panel close event
     */
    handlePanelClose(panelId) {
      debugLog('[View] Panel closed:', panelId);
    },

    /**
     * Handle panels collapse event (from click-outside)
     */
    handlePanelsCollapse() {
      debugLog('[View] Panels collapsed');
    },

    /**
     * Handle quick settings close event
     */
    handleQuickSettingsClose() {
      debugLog('[View] Quick Settings closed');
      this.$store.commit('GENERAL/SET_QUICK_SETTINGS_VISIBLE', false);
    },

    /**
     * Handle create inspect tag from corrosion analysis
     * Opens Tags panel with pre-populated data
     */
    handleCreateInspectFromCorrosion(eventData) {
      debugLog('[View] Received create-inspect-tag event:', {
        hasAnalysisResult: !!eventData.analysisResultJson,
        hasHeatMapUrl: !!eventData.heatMapUrl,
      });

      try {
        const forgeService = this.$store.state.forgeService;
        if (!forgeService) {
          debugError('[View] ForgeService not available');
          return;
        }

        const cropExtension = forgeService.getCropping3DExtension();
        if (!cropExtension) {
          debugError('[View] Cropping3DExtension not available');
          return;
        }

        const cropBox = cropExtension.getCurrentCropBox();
        if (!cropBox) {
          debugWarn('[View] No active crop box found');
        }

        var tagPoint = cropBox
          ? new THREE.Vector3(
              (cropBox.Min.x + cropBox.Max.x) / 2,
              (cropBox.Min.y + cropBox.Max.y) / 2,
              (cropBox.Min.z + cropBox.Max.z) / 2
            )
          : null;

        tagPoint = forgeService.view3dToWorld(tagPoint);

        let viewType = this.$store.state.forgeService.panoramaViewer ? '360' : '3d';

        this.$store.commit('SET_ADDING_TAG_INFO', {
          type: 'AVA Inspect',
          viewType: viewType,
          tagPoint: tagPoint,
          level2D: null,
          selection: [],
          preCapturedCropBox: cropBox,
          analysisResultJson: eventData.analysisResultJson,
          heatMapUrl: eventData.heatMapUrl,
        });

        // Open Tags panel using new navigation system
        this.$store.dispatch('GENERAL/OPEN_PANEL', 'tags');

        debugLog('[View] Tag creation panel opened with pre-populated data');
      } catch (error) {
        debugError('[View] Error handling create inspect tag:', error);
        if (this.$dialog && this.$dialog.notify) {
          this.$dialog.notify.error('Failed to create inspect tag', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      }
    },

    /**
     * Handle update inspect tag from corrosion analysis
     */
    handleUpdateInspectFromCorrosion(eventData) {
      debugLog('[View] Received update-inspect-tag event:', {
        tagId: eventData.tagId,
        hasAnalysisResult: !!eventData.analysisResultJson,
        hasHeatMapUrl: !!eventData.heatMapUrl,
      });

      try {
        const { tagId, analysisResultJson, heatMapUrl } = eventData;

        if (!tagId) {
          debugError('[View] No tagId provided for update');
          this.$dialog.notify.error('Cannot update tag: Missing tag ID', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.$store
          .dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', tagId)
          .then((existingTag) => {
            debugLog('[View] Fetched existing tag:', existingTag.id);

            existingTag.analysisResultJson = JSON.stringify(analysisResultJson);

            if (!existingTag.metadataJson && analysisResultJson?.pointCloudData?.pointCloudId) {
              const linkedScanInfo = new LinkedScanInfo();
              linkedScanInfo.pointCloudId = analysisResultJson.pointCloudData.pointCloudId;
              existingTag.metadataJson = JSON.stringify(linkedScanInfo);
              debugLog('[View] Populated metadataJson with pointCloudId:', linkedScanInfo.pointCloudId);
            }

            if (heatMapUrl) {
              existingTag.heatMapUrl = heatMapUrl;
              debugLog('[View] Setting heatMapUrl:', heatMapUrl);
            }

            debugLog('[View] Calling UPDATE_AVA_INSPECT_TAG action');

            return this.$store.dispatch('UPDATE_AVA_INSPECT_TAG', {
              tag: existingTag,
              thumbnail: null,
              cropVolume: null,
              propagateUpdates: true,
            });
          })
          .then((updatedTag) => {
            debugLog('[View] AVA Inspect tag updated successfully:', updatedTag.id);

            this.$dialog.notify.success('AVA Inspect tag updated successfully', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch((error) => {
            debugError('[View] Error updating AVA Inspect tag:', error);
            this.$dialog.notify.error('Failed to update AVA Inspect tag', {
              position: 'top-right',
              timeout: 3000,
            });
          });
      } catch (error) {
        debugError('[View] Error handling update inspect tag:', error);
        this.$dialog.notify.error('Failed to update inspect tag', {
          position: 'top-right',
          timeout: 3000,
        });
      }
    },
  },
};
</script>

<style lang="scss">
@import '../styles/main.css';

#app {
  width: 100vw;
  height: 100vh;
}

.viewer-layout-container {
  position: relative;
  height: 100%;
}

// Main content area - push-over layout
// Margin is dynamically calculated via mainContentStyle computed property
.main-content {
  // Smooth transition for margin changes
  // (also set inline via mainContentStyle for CSS variable support)
  transition: margin-left var(--ds-transition-rail);
  min-height: 100%;
}

// Mobile responsive
@media (max-width: 768px) {
  .main-content {
    // On mobile, panels are full-screen overlays - no margin needed
    margin-left: 0 !important;
  }
}
</style>
